import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { robotApi } from "../../services/robotApi";
import type { AvailabilityJob, RobotOperationStatus } from "../../types";

export type OperationNotice = {
  type: "info" | "success" | "warning" | "error";
  title: string;
  message: string;
};

const titles: Record<RobotOperationStatus, string> = {
  queued: "Robotti jonossa", starting: "Robotti käynnistyy", authenticating: "Kirjaudutaan Elisaan",
  waiting_mfa: "Odotetaan Elisan vahvistuskoodia", retry_wait: "Kirjautumisen uusintayritys ajastettu",
  closing: "Kirjautumisyritystä päätetään", processing: "Robotti käsittelee rivejä", finishing: "Ajoa viimeistellään",
  completed: "Robottiajo valmistui", completed_with_warnings: "Robottiajo valmistui huomautuksin", failed: "Robottiajo ei valmistunut"
};
export function robotActive(job: Pick<AvailabilityJob, "robotPhase">) {
  return !!job.robotPhase && !["completed", "completed_with_warnings", "failed"].includes(job.robotPhase);
}
export function robotPhaseTitle(job: Pick<AvailabilityJob, "robotPhase">) {
  return job.robotPhase ? titles[job.robotPhase] : "";
}

export function useRobotOperation(operationId: string | undefined, setNotice: (notice: OperationNotice) => void) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["robot-operation", operationId],
    queryFn: () => robotApi.getOperation(operationId!),
    enabled: !!operationId,
    refetchInterval: (q) => q.state.data && ["completed", "completed_with_warnings", "failed"].includes(q.state.data.status) ? false : 5000,
    refetchIntervalInBackground: true,
    retry: 2
  });
  useEffect(() => {
    const op = query.data;
    if (!op) return;
    setNotice({
      type: op.status === "completed" ? "success" : op.status === "failed" ? "error" : op.status === "completed_with_warnings" ? "warning" : "info",
      title: titles[op.status], message: op.message
    });
    for (const key of ["requests", "results", "logs"]) client.invalidateQueries({ queryKey: [key] });
  }, [query.data, client, setNotice]);
  return query;
}
